import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, XCircle, ExternalLink, Clipboard, X, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = [
  { key: "youtube", label: "YouTube" },
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "tiktok", label: "TikTok" },
];

function tiktokFullText(row) {
  const cap = (row.caption || "").trim();
  const tags = (row.platforms?.tiktok ? null : null);
  // hashtags live on the row; we stored them alongside caption per Phase 2.
  const hashtags = row.hashtags || [];
  const tail = hashtags.length ? "\n\n" + hashtags.join(" ") : "";
  return cap + tail;
}

function TikTokModal({ row, onClose }) {
  const [copied, setCopied] = useState(false);
  // We may not have hashtags directly on the grouped row; fall back to caption only.
  // (clips_posted writes hashtags per platform — we surface caption + hashtags as composed text.)
  const text = (() => {
    const cap = (row.caption || "").trim();
    const hashtags = row.hashtags || [];
    return hashtags.length ? `${cap}\n\n${hashtags.join(" ")}` : cap;
  })();
  const copy = () => {
    navigator.clipboard.writeText(text || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="tiktok-modal">
      <div className="w-full max-w-lg rounded-lg border border-zinc-800 bg-zinc-950 p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-zinc-500">TikTok drafts inbox</p>
            <h3 className="text-base font-medium text-zinc-100">Copy caption to paste in TikTok</h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200" aria-label="Close" data-testid="tiktok-modal-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mb-3 text-xs text-zinc-400">
          Open TikTok → Inbox → tap the new notification → paste this caption.
        </p>
        <textarea readOnly value={text || ""} rows={6}
          className="w-full resize-none rounded bg-black/40 px-3 py-2 text-sm text-zinc-200 ring-1 ring-zinc-800 focus:outline-none" />
        <button
          onClick={copy}
          data-testid="tiktok-modal-copy"
          className="mt-3 inline-flex items-center gap-1.5 rounded bg-amber-400 px-3 py-2 text-sm font-medium text-zinc-900 hover:bg-amber-300"
        >
          <Copy className="h-4 w-4" /> {copied ? "Copied!" : "Copy caption to clipboard"}
        </button>
      </div>
    </div>
  );
}

export default function History() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [modalRow, setModalRow] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/posts/history?limit=50`);
        setRows(r.data.rows || []);
      } catch (e) {
        setErr(e?.message || "load failed");
      }
    })();
  }, []);

  if (err) return <div className="text-sm text-red-300">{err}</div>;
  if (!rows) return <div className="flex items-center gap-2 text-sm text-zinc-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading history…</div>;

  return (
    <div data-testid="history-page">
      <header className="mb-6">
        <p className="mb-1 text-xs uppercase tracking-[0.2em] text-zinc-500">History</p>
        <h1 className="text-3xl font-semibold tracking-tight">Post history</h1>
        <p className="mt-2 text-sm text-zinc-400">Most recent {rows.length} posts. Already-posted platforms are skipped automatically.</p>
      </header>
      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-800 bg-zinc-900/30 p-8 text-center text-sm text-zinc-500">
          No posts yet. Go to <a className="underline" href="/clips">Clips</a> and use "Post Now".
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-3 py-2 text-left">Posted at</th>
                <th className="px-3 py-2 text-left">Clip</th>
                {PLATFORMS.map(p => <th key={p.key} className="px-3 py-2 text-center">{p.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-zinc-800 hover:bg-zinc-900/30">
                  <td className="px-3 py-2 text-xs text-zinc-400 whitespace-nowrap">{new Date(r.posted_at).toLocaleString()}</td>
                  <td className="px-3 py-2 max-w-md truncate text-zinc-200" title={r.caption}>{r.clip_id.slice(0, 8)}…</td>
                  {PLATFORMS.map(p => {
                    const cell = r.platforms[p.key];
                    if (!cell) return <td key={p.key} className="px-3 py-2 text-center text-zinc-700">—</td>;
                    if (cell.status === "success") {
                      // Special TikTok cell — opens modal with copy helper
                      if (p.key === "tiktok") {
                        return (
                          <td key={p.key} className="px-3 py-2 text-center">
                            <button
                              onClick={() => setModalRow(r)}
                              data-testid={`tiktok-cell-${i}`}
                              className="inline-flex items-center gap-1 rounded bg-amber-400/10 px-2 py-1 text-[11px] text-amber-200 ring-1 ring-amber-400/30 hover:bg-amber-400/20"
                            >
                              <Clipboard className="h-3 w-3" /> In TikTok inbox — copy caption
                            </button>
                          </td>
                        );
                      }
                      return (
                        <td key={p.key} className="px-3 py-2 text-center">
                          {cell.platform_url && cell.platform_url.startsWith("http") ? (
                            <a href={cell.platform_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200">
                              <CheckCircle2 className="h-4 w-4" /><ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-emerald-300" title={cell.platform_url || "posted"}>
                              <CheckCircle2 className="h-4 w-4" />
                            </span>
                          )}
                        </td>
                      );
                    }
                    if (cell.status === "skipped") {
                      return <td key={p.key} className="px-3 py-2 text-center text-zinc-500" title="already posted">↷</td>;
                    }
                    return (
                      <td key={p.key} className="px-3 py-2 text-center" title={cell.error || "failed"}>
                        <XCircle className="inline h-4 w-4 text-red-400" />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {modalRow && <TikTokModal row={modalRow} onClose={() => setModalRow(null)} />}
    </div>
  );
}
